import throttle from 'lodash/throttle';
import { RefObject, useCallback, useEffect, useState } from 'react';

import { HEADER_OFFSET, NavOption } from '@components/pages/Organization/types';

interface UseScrollerPositionProps {
  navRef: RefObject<HTMLDivElement>;
  scrollerRef: RefObject<HTMLDivElement>;
}

const WAIT_FOR_INITIAL_SCROLLCHECK = 1000;

const useScrollerPosition = ({ navRef, scrollerRef }: UseScrollerPositionProps) => {
  const [activeSection, setActiveSection] = useState(NavOption.Account);
  const [scrollerPosition, setScrollerPosition] = useState(0);

  const calculateSectionProgress = (
    scrollPosition: number,
    sectionStart: number,
    sectionHeight: number
  ) => {
    return Math.max(0, Math.min(1, (scrollPosition - sectionStart) / sectionHeight));
  };

  const handleActiveSection = (scrollPosition: number, maxPosition: number) => {
    if (scrollPosition < maxPosition / 4) {
      setActiveSection(NavOption.Account);
    } else if (scrollPosition < (maxPosition * 2) / 4) {
      setActiveSection(NavOption.Admins);
    } else if (scrollPosition < (maxPosition * 3) / 4) {
      setActiveSection(NavOption.Users);
    } else {
      setActiveSection(NavOption.Analytics);
    }
  };

  const handleScrollerPosition = (
    maxPosition: number,
    accountScrollProgress: number,
    adminsScrollProgress: number,
    usersScrollProgress: number
  ): number => {
    const sectionLength = maxPosition / 3;
    let newPosition;

    if (accountScrollProgress < 1) {
      newPosition = Math.round(sectionLength * accountScrollProgress);
    } else if (adminsScrollProgress < 1) {
      newPosition = Math.round(sectionLength * (1 + adminsScrollProgress));
    } else {
      newPosition = Math.round(sectionLength * (2 + usersScrollProgress));
    }

    newPosition = Math.min(newPosition, maxPosition);
    setScrollerPosition(newPosition);
    return newPosition;
  };

  const handleScroll = useCallback(() => {
    if (!navRef.current || !scrollerRef.current) return;

    const accountElement = document.getElementById(NavOption.Account);
    const adminsElement = document.getElementById(NavOption.Admins);
    const usersElement = document.getElementById(NavOption.Users);
    const analyticsElement = document.getElementById(NavOption.Analytics);

    if (!accountElement || !adminsElement || !usersElement || !analyticsElement) return;

    const { height: navHeight } = navRef.current.getBoundingClientRect();
    const { height: scrollerHeight } = scrollerRef.current.getBoundingClientRect();
    const maxPosition = navHeight - scrollerHeight;
    const accountHeight = adminsElement?.offsetTop - accountElement?.offsetTop;
    const adminsHeight = usersElement?.offsetTop - adminsElement?.offsetTop;
    const usersHeight = analyticsElement?.offsetTop - usersElement?.offsetTop;
    const scrollPosition = window.scrollY - accountElement?.offsetTop - HEADER_OFFSET;

    const accountScrollProgress = calculateSectionProgress(scrollPosition, 0, accountHeight);
    const adminsScrollProgress = calculateSectionProgress(
      scrollPosition,
      accountHeight,
      adminsHeight
    );
    const usersScrollProgress = calculateSectionProgress(
      scrollPosition,
      accountHeight + adminsHeight,
      usersHeight
    );

    const newPosition = handleScrollerPosition(
      maxPosition,
      accountScrollProgress,
      adminsScrollProgress,
      usersScrollProgress
    );
    handleActiveSection(newPosition, maxPosition);
  }, [navRef, scrollerRef]);

  const scrollToSection = (section: NavOption) => {
    const element = document.getElementById(section);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      window.scrollTo({
        left: 0,
        top: elementPosition - HEADER_OFFSET,
        behavior: 'smooth',
      });
    }
  };

  // Auto-scroll to any hash that might exist already
  useEffect(() => {
    window.setTimeout(() => {
      scrollToSection(window.location.hash.slice(1) as NavOption);
    }, WAIT_FOR_INITIAL_SCROLLCHECK);
  }, []);

  useEffect(() => {
    const throttledHandleScroll = throttle(handleScroll, 100);
    window.addEventListener('scroll', throttledHandleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', throttledHandleScroll, {
        passive: true,
      } as AddEventListenerOptions);
    };
  }, [handleScroll]);

  return { activeSection, scrollerPosition, scrollToSection };
};

export default useScrollerPosition;
